import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../layout/MainLayout';
import Card, { CardHeader, CardTitle, CardBody } from '../core/Card/Card';
import { Calendar, Clock, Video, MapPin, FileText, Users, ArrowLeft, Save } from 'lucide-react';
import { apiService } from '../../services/api';
import '../../styles/mentor-session-form.scss';

interface Mentee {
  id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface SessionFormData {
  relationship_id: string;
  scheduled_at: string;
  duration_minutes: number;
  topic: string;
  description: string;
  location: string;
  meeting_link: string;
  session_type: 'presential' | 'virtual';
}

const NewSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mentees, setMentees] = useState<Mentee[]>([]);
  const [formData, setFormData] = useState<SessionFormData>({
    relationship_id: '',
    scheduled_at: '',
    duration_minutes: 60,
    topic: '',
    description: '',
    location: '',
    meeting_link: '',
    session_type: 'virtual'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadMentees();
  }, []);

  const loadMentees = async () => {
    try {
      const response = await apiService.mentor.getMentees();
      const menteesData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      setMentees(menteesData);
    } catch (error) {
      console.error('Error loading mentees:', error);
      setMentees([]);
    }
  };

  const handleChange = (field: keyof SessionFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.relationship_id) {
      newErrors.relationship_id = 'Debes seleccionar un mentee';
    }
    if (!formData.scheduled_at) {
      newErrors.scheduled_at = 'La fecha y hora son requeridas';
    }
    if (!formData.topic.trim()) {
      newErrors.topic = 'El tema es requerido';
    }
    if (formData.duration_minutes < 15 || formData.duration_minutes > 240) {
      newErrors.duration_minutes = 'La duración debe estar entre 15 y 240 minutos';
    }
    if (formData.session_type === 'virtual' && !formData.meeting_link.trim()) {
      newErrors.meeting_link = 'El link de reunión es requerido para sesiones virtuales';
    }
    if (formData.session_type === 'presential' && !formData.location.trim()) {
      newErrors.location = 'La ubicación es requerida para sesiones presenciales';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const submitData = {
        ...formData,
        duration_minutes: Number(formData.duration_minutes)
      };

      await apiService.mentor.createSession(submitData);

      navigate('/mentor/sessions');
    } catch (error: any) {
      console.error('Error creating session:', error);
      const errorMessage = error.response?.data?.message || 'Error al crear la sesión';
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/mentor/sessions');
  };

  return (
    <MainLayout>
      <div className="session-form-page">
        {/* Header */}
        <div className="page-header">
          <button className="btn-back" onClick={handleCancel}>
            <ArrowLeft size={20} />
          </button>
          <div className="page-header__info">
            <div className="eyebrow">Mentor · Nueva Sesión</div>
            <h1 className="heading-1">Programar Nueva Sesión</h1>
            <p className="page-description">
              Crea una nueva sesión de mentoría con tus mentees
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="session-form">
          <div className="form-grid">
            {/* Left Column - Main Info */}
            <div className="form-column">
              <Card>
                <CardHeader>
                  <CardTitle>Información de la sesión</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="form-group">
                    <label>
                      <Users size={16} />
                      Mentee *
                    </label>
                    <select
                      value={formData.relationship_id}
                      onChange={(e) => handleChange('relationship_id', e.target.value)}
                      className={errors.relationship_id ? 'error' : ''}
                    >
                      <option value="">Selecciona un mentee</option>
                      {mentees.map(mentee => (
                        <option key={mentee.id} value={mentee.id}>
                          {mentee.first_name} {mentee.last_name}
                        </option>
                      ))}
                    </select>
                    {errors.relationship_id && (
                      <span className="error-message">{errors.relationship_id}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>
                      <FileText size={16} />
                      Tema de la sesión *
                    </label>
                    <input
                      type="text"
                      value={formData.topic}
                      onChange={(e) => handleChange('topic', e.target.value)}
                      placeholder="Ej: Revisión de progreso trimestral"
                      className={errors.topic ? 'error' : ''}
                    />
                    {errors.topic && (
                      <span className="error-message">{errors.topic}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>
                      <FileText size={16} />
                      Descripción
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Describe los objetivos y temas a tratar en esta sesión..."
                      rows={4}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <Calendar size={16} />
                        Fecha y hora *
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.scheduled_at}
                        onChange={(e) => handleChange('scheduled_at', e.target.value)}
                        className={errors.scheduled_at ? 'error' : ''}
                      />
                      {errors.scheduled_at && (
                        <span className="error-message">{errors.scheduled_at}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>
                        <Clock size={16} />
                        Duración (minutos) *
                      </label>
                      <input
                        type="number"
                        value={formData.duration_minutes}
                        onChange={(e) => handleChange('duration_minutes', e.target.value)}
                        min="15"
                        max="240"
                        step="15"
                        className={errors.duration_minutes ? 'error' : ''}
                      />
                      {errors.duration_minutes && (
                        <span className="error-message">{errors.duration_minutes}</span>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Right Column - Location & Type */}
            <div className="form-column">
              <Card>
                <CardHeader>
                  <CardTitle>Tipo y ubicación</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="form-group">
                    <label>Tipo de sesión *</label>
                    <div className="session-type-options">
                      <button
                        type="button"
                        className={`type-option ${formData.session_type === 'virtual' ? 'active' : ''}`}
                        onClick={() => handleChange('session_type', 'virtual')}
                      >
                        <Video size={20} />
                        <span>Virtual</span>
                      </button>
                      <button
                        type="button"
                        className={`type-option ${formData.session_type === 'presential' ? 'active' : ''}`}
                        onClick={() => handleChange('session_type', 'presential')}
                      >
                        <MapPin size={20} />
                        <span>Presencial</span>
                      </button>
                    </div>
                  </div>

                  {formData.session_type === 'virtual' && (
                    <div className="form-group">
                      <label>
                        <Video size={16} />
                        Link de reunión *
                      </label>
                      <input
                        type="url"
                        value={formData.meeting_link}
                        onChange={(e) => handleChange('meeting_link', e.target.value)}
                        placeholder="https://zoom.us/j/123456789"
                        className={errors.meeting_link ? 'error' : ''}
                      />
                      {errors.meeting_link && (
                        <span className="error-message">{errors.meeting_link}</span>
                      )}
                      <small>Google Meet, Zoom, Teams, etc.</small>
                    </div>
                  )}

                  {formData.session_type === 'presential' && (
                    <div className="form-group">
                      <label>
                        <MapPin size={16} />
                        Ubicación *
                      </label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => handleChange('location', e.target.value)}
                        placeholder="Oficina, sala de juntas, cafetería..."
                        className={errors.location ? 'error' : ''}
                      />
                      {errors.location && (
                        <span className="error-message">{errors.location}</span>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Tips Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Consejos para la sesión</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="tips-list">
                    <div className="tip-item">
                      <FileText size={16} />
                      <p>Define objetivos claros antes de la sesión</p>
                    </div>
                    <div className="tip-item">
                      <Clock size={16} />
                      <p>Agenda suficiente tiempo para discusión</p>
                    </div>
                    <div className="tip-item">
                      <Calendar size={16} />
                      <p>Confirma la disponibilidad con tu mentee</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            {errors.submit && (
              <div className="error-banner">
                {errors.submit}
              </div>
            )}
            <button
              type="button"
              className="btn btn--outline"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={loading}
            >
              {loading ? (
                <>Creando sesión...</>
              ) : (
                <>
                  <Save size={18} />
                  Crear sesión
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default NewSessionPage;
